<template>
  <component
    :is="label ? 'fieldset' : 'div'"
    :class="_class"
    :role="label ? null : 'group'"
    :aria-label="offscreenLabel"
    :aria-describedby="id ? id + '-code-entry-offscreen-desc' : null"
  >
    <legend v-if="label" :id="id + '_leg'">{{ label }}</legend>
    <cat-help-trigger
      v-if="help && help.url"
      :sourceId="id"
      :url="help.url"
      :title="label"
    />
    <cat-hint :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="help && help.text"
      :id="id"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_leg' : null"
    />
    <div :id="id + '-code-entry-offscreen-desc'" style="display: none">
      {{
        resolveTxt(
          "skin.code.entry.offscreenDesc",
          "Each code digit is entered individually per input field. The focus will move to the next field after each digit entry."
        )
      }}
    </div>
    <div class="code-entry-holder" tabindex="-1">
      <template v-for="index in parseInt(length)" :key="index + 1 + 'input'">
        <input
          :id="id + '-code-entry' + index"
          type="text"
          :class="inputClass"
          size="1"
          maxlength="1"
          :aria-required="true"
          :disabled="readonly"
          :aria-disabled="readonly ? 'true' : null"
          :aria-label="getLabel(index, length)"
          :data-label="getLabel(index, length)"
          @input="nextFreeField"
          @keyup.delete="prevEnteredField"
          @keypress.enter.stop="submit"
          @blur="onDigitBlur"
          @paste.prevent="onPaste"
        />
      </template>
    </div>
    <slot></slot>
    <cat-errors v-if="errors" :errors="errors" />
  </component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import jQuery from "jquery";
import CatHelp from "@/components/utilities/help.vue";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger.vue";
import CatHint from "@/components/utilities/hint.vue";
import CatErrors from "@/components/catErrors/catErrors.vue";

export default defineComponent({
  components: { CatHelp, CatHint, CatErrors, CatHelpTrigger },
  name: "CatCodeEntry",
  inject: {
    resolveTxt: { name: "resolveTxt", default: (key, def) => def }
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
      description: "node id + -code-entry + index"
    },
    length: {
      type: [Number, String], // Really this is only a number but the UI hub preview passes a string
      required: true,
      description: "Number of code digits."
    },
    label: {
      type: String,
      description: "The code entry group legend."
    },
    offscreenLabel: {
      type: String,
      description:
        "The label of the code entry to be used by screen readers. Additional offscreen code entry description can be updated in the text key 'skin.code.entry.offscreenDesc'. Each code digit label can be updated in the text key 'skin.code.entry.digit.[X].label'"
    },
    mask: {
      type: String,
      default: "\\d",
      description: "Regex that is used to mask the characters"
    },
    errors: {
      type: Array,
      description: "List of errors"
    },
    readonly: {
      type: Boolean,
      default: false,
      description: "Set to true to disable the input field digits."
    },
    fireChangeOnComplete: {
      type: Boolean,
      default: false,
      description:
        "Whether to send the ui-change event once all the digits are filled in even without losing focus of the digits."
    },
    help: {
      type: Object,
      default: undefined,
      description: "Help text for code entry"
    },
    hint: {
      type: String,
      default: undefined,
      description: "Hint text for code entry"
    }
  },
  methods: {
    submit: function (event) {
      // Move the focus out of the digits to the holder parent container
      // to trigger the blur of the code entry digits
      // This prevents the blur (event) to be sent again causing
      // duplicate change attribute value requests to be submitted.
      jQuery(".code-entry-holder", this.$el).focus();
      this._fireUpdate("ui-submit", event);
    },
    nextFreeField: function (event) {
      if (
        event.target == null ||
        event.target.value == null ||
        !event.target.value.match(this.regexMatch)
      ) {
        // an invalid character was entered so we ignore it
        event.target.value = "";
        return;
      }

      var nextField = jQuery(event.target).nextAll("input");
      if (nextField.length === 0) {
        nextField = jQuery("input", this.$el).last();
      }
      jQuery(nextField[0]).focus().select();

      // Check if the code is submitting the change event once all the digits are filled in
      if (this.fireChangeOnComplete) {
        const code = this.getCodeValue();
        if (code.length >= this.length) {
          // Move the focus out of the digits to trigger the blur event
          jQuery(".code-entry-holder", this.$el).focus();
        }
      }
    },
    prevEnteredField: function (event) {
      var prev = jQuery(event.target).prev("input");
      prev.focus().select();
    },
    getCodeValue: function () {
      let code = "";
      jQuery("input", this.$el)
        .filter((_, item) => {
          return item.value.length > 0;
        })
        .each((_, item) => (code += item.value));
      return code;
    },
    _fireUpdate: function (eventType, event) {
      const code = this.getCodeValue();
      this.$emit(eventType, {
        event: event,
        id: this.id ? this.id : null,
        code: code,
        value: code
      });
    },
    onDigitBlur: function (event) {
      // On blur of the digit input fields, check if the focus is still within the other of digits.
      // If the new focus (relatedTarget) is null or is outside the input digits,
      // it means the user is done with the code entry, we can fire the ui-change event.

      const _this = this;
      let isFocusInDigitInput = false;
      if (event.relatedTarget) {
        jQuery("input", _this.$el).each(function (i, el) {
          if (el === event.relatedTarget) {
            isFocusInDigitInput = true;
          }
        });
      }

      if (!isFocusInDigitInput) {
        this._fireUpdate("ui-change", event);
      }
    },
    onPaste: function (event) {
      var self = this;
      var text = (event.clipboardData || window.clipboardData).getData("text");
      jQuery("input", this.$el).each((_, item) => {
        if (text.length > 0) {
          var singleChar = text.slice(0, 1);
          if (singleChar.match(self.regexMatch)) {
            item.value = singleChar;
          }
          text = text.substr(1);
        }
      });
      this.nextFreeField(event);
    },
    getLabel: function (index, length) {
      let label = this.resolveTxt("skin.code.entry.digit", "Digit {0} of {1}");
      label = label.replace(/\{0}/g, index);
      return label.replace(/\{1}/g, length);
    }
  },
  computed: {
    _class: function () {
      let css = ["cat-code-entry", "fieldset"];
      if (this.help && this.help.url) {
        css.push("has-advanced-help");
      }
      if (this.status) {
        css.push(this.status);
      }
      return css;
    },
    inputClass: function () {
      return {
        "code-entry-input": true,
        invalidField: this.status === "invalid"
      };
    },
    status: function () {
      let t = this;
      // No errors mean not validated no status
      if (
        this.errors === null ||
        typeof this.errors === "undefined" ||
        this.errors.length === 0
      ) {
        return undefined;
      }

      let anyError = function (level) {
        return t.errors.some(function (err) {
          return err.level === level;
        });
      };
      if (anyError(0) || anyError(1)) {
        return "invalid";
      }
      if (anyError(2)) {
        return "warn";
      }
      if (anyError(3)) {
        return "info";
      }
      if (anyError(4)) {
        return "success";
      }
      // There are errors but all the levels are > 4
      return "default";
    },
    regexMatch: function () {
      return RegExp(this.mask);
    }
  },
  created: function () {
    // Automatically select the text on focus
    jQuery("input", this.$el).focus(function () {
      jQuery(this).select();
    });
  }
});
</script>
